import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LogOut, Package, Plus, Calculator, FileSignature, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import CustomerShipmentForm from "@/components/CustomerShipmentForm";
import ShipmentTimeline from "@/components/ShipmentTimeline";
import BrandLogo from "@/components/BrandLogo";
import { formatLocalAddress } from "@/lib/utils";

const cls = "bg-background border-border font-mono";
const PRIORITIES = ["Direct", "HOT", "Rush", "Regular", "Sameday", "NextDay"];

function StatusBadge({ status }) {
  const map = {
    pending: { cls: "bg-brand-warning/20 text-brand-warning border-brand-warning", label: "Delivery Scheduled" },
    assigned: { cls: "bg-brand-warning/20 text-brand-warning border-brand-warning", label: "Delivery Scheduled" },
    picked_up: { cls: "bg-purple-500/20 text-purple-300 border-purple-500", label: "Picked Up" },
    in_transit: { cls: "bg-brand-green/20 text-brand-green border-brand-green", label: "In Transit" },
    delivered: { cls: "bg-brand-success/20 text-brand-success border-brand-success", label: "Delivered" },
    cancelled: { cls: "bg-destructive/20 text-destructive border-destructive", label: "Cancelled" },
  };
  const entry = map[status] || { cls: "", label: status };
  return <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${entry.cls}`}>{entry.label}</span>;
}

function QuoteDialog() {
  const [f, setF] = useState({ service_priority: "Regular", weight_kg: 1, pieces: 1, pickup_address: "", dropoff_address: "" });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const calc = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/quote", {
        service_priority: f.service_priority,
        weight_kg: Number(f.weight_kg),
        pieces: Number(f.pieces),
        pickup_address: f.pickup_address || null,
        dropoff_address: f.dropoff_address || null,
      });
      setQuote(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog onOpenChange={(o) => { if (!o) setQuote(null); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="font-mono uppercase text-xs" data-testid="get-quote-btn"><Calculator className="w-3 h-3 mr-2"/>Get Quote</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto bg-card border-2 border-border">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-tighter text-2xl">Get a Quote</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="font-mono text-[10px] uppercase tracking-widest">Service Priority</Label>
            <Select value={f.service_priority} onValueChange={(v) => setF({ ...f, service_priority: v })}>
              <SelectTrigger className={cls} data-testid="quote-priority"><SelectValue/></SelectTrigger>
              <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="font-mono text-[10px] uppercase tracking-widest">Weight (kg)</Label><Input type="number" step="0.1" value={f.weight_kg} onChange={(e)=>setF({...f,weight_kg:e.target.value})} className={cls} data-testid="quote-weight"/></div>
            <div className="space-y-1"><Label className="font-mono text-[10px] uppercase tracking-widest">Pieces</Label><Input type="number" value={f.pieces} onChange={(e)=>setF({...f,pieces:e.target.value})} className={cls} data-testid="quote-pieces"/></div>
          </div>
          <Input placeholder="Pickup address (optional)" value={f.pickup_address} onChange={(e)=>setF({...f,pickup_address:e.target.value})} className={cls} data-testid="quote-pickup"/>
          <Input placeholder="Delivery address (optional)" value={f.dropoff_address} onChange={(e)=>setF({...f,dropoff_address:e.target.value})} className={cls} data-testid="quote-dropoff"/>
          <Button disabled={busy} onClick={calc} className="w-full h-11 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase font-bold" data-testid="quote-calc-btn">{busy ? "Calculating…" : "Calculate Estimate"}</Button>

          {quote && (
            <div className="border-t border-dashed border-border pt-3 mt-1 font-mono text-xs space-y-1" data-testid="quote-result">
              <div className="flex justify-between"><span>Base Rate</span><span>${quote.pricing.base_rate.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Fuel Surcharge ({quote.pricing.fuel_surcharge_pct}%)</span><span>${quote.pricing.fuel_surcharge.toFixed(2)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>${quote.pricing.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>GST ({quote.pricing.gst_rate}%)</span><span>${quote.pricing.gst.toFixed(2)}</span></div>
              <div className="border-t-2 border-foreground pt-2 mt-2 flex justify-between text-base">
                <span className="font-display uppercase">Estimated Total</span>
                <span className="text-brand-green font-bold" data-testid="quote-total">${quote.pricing.total.toFixed(2)}</span>
              </div>
              <div className="text-muted-foreground text-[10px] uppercase tracking-widest pt-2">{quote.note}</div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [detailOpen, setDetailOpen] = useState(null);
  const [podOpen, setPodOpen] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/deliveries");
      setDeliveries(data.deliveries);
      setDetailOpen((cur) => cur ? (data.deliveries.find((d) => d.id === cur.id) || cur) : cur);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between bg-card flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <BrandLogo className="h-9" />
          <div>
            <div className="font-display text-lg uppercase tracking-tight" data-testid="account-name">{user.account_name || user.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Hello, {user.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <QuoteDialog />
          <Dialog open={newOpen} onOpenChange={setNewOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase text-xs font-bold" data-testid="new-shipment-btn"><Plus className="w-3 h-3 mr-2"/>New Shipment</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-2 border-border">
              <DialogHeader><DialogTitle className="font-display uppercase tracking-tighter text-2xl">New Shipment</DialogTitle></DialogHeader>
              <CustomerShipmentForm onCreated={() => { setNewOpen(false); load(); }} />
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={() => { logout(); navigate("/"); }} className="font-mono uppercase text-xs" data-testid="logout-btn"><LogOut className="w-3 h-3 mr-2"/>Out</Button>
        </div>
      </header>

      <div className="flex-1 max-w-6xl w-full mx-auto p-6">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">My Shipments</div>
        <div className="border border-border bg-card">
          {deliveries.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5}/>
              <div className="font-display uppercase text-lg tracking-tight">No shipments yet</div>
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mt-2">Click NEW SHIPMENT to get started</div>
            </div>
          ) : (
            <Table data-testid="shipments-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest">Date Scheduled</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest">Tracking #</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest">Pickup</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest">Delivery</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest">Status</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest text-right">Price</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest text-center">POD</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-widest text-center">Photo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.map((s) => {
                  const hasPod = !!s.signature_data_url;
                  const hasPhoto = !!s.photo_data_url;
                  const scheduled = s.delivery_date ? new Date(s.delivery_date).toLocaleDateString() : "—";
                  return (
                    <TableRow key={s.id} className="border-border" data-testid={`order-row-${s.delivery_number}`}>
                      <TableCell className="font-mono text-xs">{scheduled}</TableCell>
                      <TableCell>
                        <button
                          onClick={() => setDetailOpen(s)}
                          className="font-mono text-xs text-brand-green font-bold tracking-widest hover:underline"
                          data-testid={`track-${s.delivery_number}`}
                        >
                          {s.delivery_number}
                        </button>
                      </TableCell>
                      <TableCell className="text-xs max-w-[220px] truncate" title={formatLocalAddress(s.pickup.address)}>{formatLocalAddress(s.pickup.address)}</TableCell>
                      <TableCell className="text-xs max-w-[220px] truncate" title={formatLocalAddress(s.dropoff.address)}>{formatLocalAddress(s.dropoff.address)}</TableCell>
                      <TableCell><StatusBadge status={s.status} /></TableCell>
                      <TableCell className="font-mono text-xs text-right">${(s.pricing?.total ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPodOpen(s)}
                          disabled={!hasPod}
                          className="font-mono uppercase text-[10px] h-7"
                          data-testid={`pod-btn-${s.delivery_number}`}
                        >
                          <FileSignature className="w-3 h-3 mr-1" /> View
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPhotoOpen(s)}
                          disabled={!hasPhoto}
                          className="font-mono uppercase text-[10px] h-7"
                          data-testid={`photo-btn-${s.delivery_number}`}
                        >
                          <ImageIcon className="w-3 h-3 mr-1" /> View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Shipment details (status timeline, no live map) */}
      <Dialog open={!!detailOpen} onOpenChange={(o) => !o && setDetailOpen(null)}>
        <DialogContent className="bg-card border-2 border-border max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-tighter text-2xl">Tracking {detailOpen?.delivery_number}</DialogTitle></DialogHeader>
          {detailOpen && (
            <div className="space-y-4" data-testid="detail-content">
              <div className="flex items-start justify-between">
                <div className="font-display text-lg uppercase tracking-tight">{formatLocalAddress(detailOpen.dropoff.address)}</div>
                <StatusBadge status={detailOpen.status} />
              </div>
              <ShipmentTimeline shipment={detailOpen} />
              <div className="font-mono text-xs border-t border-dashed border-border pt-3 space-y-1">
                <div><span className="text-muted-foreground uppercase tracking-widest text-[10px]">Pickup</span><div>{formatLocalAddress(detailOpen.pickup.address)}</div></div>
                <div className="pt-1"><span className="text-muted-foreground uppercase tracking-widest text-[10px]">Delivery</span><div>{formatLocalAddress(detailOpen.dropoff.address)}</div></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Proof of delivery dialog */}
      <Dialog open={!!podOpen} onOpenChange={(o) => !o && setPodOpen(null)}>
        <DialogContent className="bg-card border-2 border-border max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-tighter text-2xl">Proof of Delivery</DialogTitle></DialogHeader>
          {podOpen && (() => {
            const delivered = (podOpen.history || []).find((h) => h.status === "delivered");
            return (
              <div className="space-y-4 font-mono text-xs" data-testid="pod-content">
                <div className="flex justify-between border-b border-dashed border-border pb-2">
                  <span className="text-muted-foreground uppercase tracking-widest text-[10px]">Tracking</span>
                  <span className="text-brand-green font-bold tracking-widest">{podOpen.delivery_number}</span>
                </div>
                <div>
                  <div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">Received by</div>
                  <div className="font-display uppercase text-base tracking-tight">{podOpen.recipient_name || "—"}</div>
                </div>
                <div>
                  <div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-1">Delivered at</div>
                  <div>{delivered ? new Date(delivered.at).toLocaleString() : "—"}</div>
                </div>
                <div>
                  <div className="text-muted-foreground uppercase tracking-widest text-[10px] mb-2">Signature</div>
                  {podOpen.signature_data_url ? (
                    <img src={podOpen.signature_data_url} alt="Signature" className="w-full border border-border bg-white" data-testid="pod-signature" />
                  ) : (
                    <div className="border border-dashed border-border p-6 text-center text-muted-foreground uppercase tracking-widest">No signature on file</div>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delivery photo dialog */}
      <Dialog open={!!photoOpen} onOpenChange={(o) => !o && setPhotoOpen(null)}>
        <DialogContent className="bg-card border-2 border-border max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-tighter text-2xl">Delivery Photo</DialogTitle></DialogHeader>
          {photoOpen && (
            <div className="space-y-3 font-mono text-xs" data-testid="photo-content">
              <div className="flex justify-between border-b border-dashed border-border pb-2">
                <span className="text-muted-foreground uppercase tracking-widest text-[10px]">Tracking</span>
                <span className="text-brand-green font-bold tracking-widest">{photoOpen.delivery_number}</span>
              </div>
              {photoOpen.photo_data_url ? (
                <img src={photoOpen.photo_data_url} alt="Delivery" className="w-full border border-border" data-testid="photo-image" />
              ) : (
                <div className="border border-dashed border-border p-6 text-center text-muted-foreground uppercase tracking-widest">No photo on file</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
